import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';
import Tag from '../Tag/Tag';
import { TextHighlight } from '../TextViewer/Highlights';
import { Icons } from '@/assets';
import { useFile } from '@/services/api/actions/useFile';
import { FileModifications } from '@/services/api/commonSchemas';
import { alertError } from '@/utils/alertError';
import { getScoreHue } from '@/utils/getScoreHue';

type Props = {
  documentId: string;
  fileModifications: FileModifications;
  documentUnitId: string;
  inactive?: boolean;
  showPageNumberBR?: boolean;
  showPageNumberTR?: boolean;
  noTextLayer?: boolean;
  width?: number;
  scale?: number;
  hasLoaded: boolean;
  onLoad: () => void;
  loadingLogoHeight?: number;
  adjustColorToScore?: number;
  score?: number;
  zoom?: number;
};

const isSafari = (): boolean => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const IS_SAFARI = isSafari();

export const PDFPreview: FC<Props> = ({
  documentId,
  fileModifications,
  documentUnitId,
  showPageNumberBR,
  showPageNumberTR,
  noTextLayer,
  width,
  scale = 1,
  loadingLogoHeight,
  hasLoaded,
  onLoad,
  adjustColorToScore,
  inactive,
  score,
  // zoom changes the size of the overlayed highlights
  zoom = 1,
}) => {
  const { t } = useTranslation();
  const { data: pdfExcerptBlob } = useFile(
    documentId,
    documentUnitId,
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      pageOverrides: (fileModifications.pageOverrides || []).map(({ highlights, ...rest }) => ({
        ...rest,
        highlights: [],
      })),
      pageSelection: fileModifications.pageSelection,
    },
    !inactive,
  );
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const highlightHue = 110;
  const rotateBy = adjustColorToScore ? getScoreHue(adjustColorToScore) - highlightHue : null;

  const onError = (error: unknown) => {
    alertError(error, { component: 'PDFPreview' }, { fileModifications });
  };

  useEffect(() => {
    if (!pdfExcerptBlob) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64data = reader.result;
      if (typeof base64data === 'string') {
        setPdfBase64(base64data);
      }
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
    };

    reader.readAsDataURL(pdfExcerptBlob);
  }, [pdfExcerptBlob]);

  const onLoadSuccess = () => {
    if (IS_SAFARI) {
      setTimeout(() => {
        onLoad();
      }, 1000);
    } else {
      onLoad();
    }
  };

  const pageModifications = fileModifications.pageOverrides?.[0];

  return (
    <div
      className={`flex relative`}
      style={{
        minWidth: width,
        height: hasLoaded ? undefined : loadingLogoHeight,
        filter: rotateBy ? `hue-rotate(${Math.floor(rotateBy)}deg)` : undefined,
      }}>
      {!inactive && (
        <Document
          loading={<div />}
          noData={<div />}
          error={t('searchResult.previewError')}
          onError={onError}
          onLoadError={onError}
          onSourceError={onError}
          onLoadSuccess={onLoadSuccess}
          file={pdfBase64}>
          <Page
            loading={<></>}
            pageNumber={1}
            width={width}
            scale={scale}
            renderAnnotationLayer={false}
            renderTextLayer={!noTextLayer}
          />
        </Document>
      )}
      {hasLoaded && (
        <div className={`top-0 left-0 mr-auto ml-auto absolute`} style={{ width: width || 100 }}>
          {pageModifications &&
            pageModifications.highlights.map(({ rect }, index) => (
              <TextHighlight
                key={`text-${index}`}
                rects={[
                  {
                    x0: (rect.x0 - (pageModifications.cropping?.x0 || 0)) * zoom,
                    x1: (rect.x1 - (pageModifications.cropping?.x0 || 0)) * zoom,
                    y0: (rect.y0 - (pageModifications.cropping?.y0 || 0)) * zoom,
                    y1: (rect.y1 - (pageModifications.cropping?.y0 || 0)) * zoom,
                  },
                ]}
              />
            ))}
        </div>
      )}
      {loadingLogoHeight && (
        <div
          className={`flex absolute bg-white w-full h-full pointer-events-none ${hasLoaded ? 'animate-fade-out-fast' : ''}`}>
          <div
            className="w-full flex items-center justify-center"
            style={{ height: loadingLogoHeight }}>
            <Icons.LogoText className={`h-4 animate-pulse`} />
          </div>
        </div>
      )}
      {showPageNumberBR &&
        fileModifications?.pageOverrides &&
        fileModifications.pageOverrides.length > 0 && (
          <Tag className={`absolute right-1 bottom-1 bg-green-100 text-green-700`}>
            {t('searchResult.pageNumberShort')} {fileModifications.pageOverrides[0].pageNumber + 1}
          </Tag>
        )}
      {showPageNumberTR &&
        fileModifications?.pageOverrides &&
        fileModifications.pageOverrides.length > 0 && (
          <Tag className={`absolute right-1 bottom-1 bg-green-100 text-green-700`}>
            {t('searchResult.pageNumberShort')} {fileModifications.pageOverrides[0].pageNumber + 1}
          </Tag>
        )}
      {score && (
        <Tag className={`absolute left-1 top-1 bg-green-100 text-green-700`}>
          {Math.round(score * 100)} %
        </Tag>
      )}
    </div>
  );
};
